// Zustand store for the ProjectTree's interactive state (expanded set,
// selected node, in-flight drag info).
//
// IMPORTANT: every selector here MUST return a PRIMITIVE or a STABLE
// reference. Returning a fresh object (e.g. `s => ({ expanded, selected })`)
// triggers a re-render on every store update, even when nothing changed —
// the same render-storm bug the CreatorStudio's comment warns about.
//
// Persistence: expanded set + selectedId are mirrored to localStorage under
// `worksnip.projectTree.expanded` and `…selectedId` so the user's mental
// model survives a refresh. Drag state is intentionally NOT persisted.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { projectApi, guideTreeApi } from '../services/projectApi';
import { flowsnipApi, type DiagramMeta } from '../services/flowsnipApi';
import { compareGuides } from '../components/tree/numbering';
import type { Project, ProjectNode, GuideLeaf, DiagramLeaf } from '../types/project';

interface DropTarget {
  /** The id of the row the cursor is hovering over. */
  id: string;
  /** Whether the target is a folder (project) or a leaf (guide).
   *  Only folders accept the 'inside' edge. */
  targetKind: 'project' | 'guide';
  /** Which third of the row — before/after for reorder, inside for re-parent
   *  (folders only). */
  edge: 'before' | 'after' | 'inside';
}

interface DragSource {
  /** Discriminator so the drop resolver knows which API to call. */
  kind: 'project' | 'guide';
  id: string;
}

/** BP 54 — Projects page view mode. Shared contract: the store owns it,
 *  ProjectsPage reads/writes it, AppShell reads it to decide whether the
 *  left rail should scope the panel instead of navigating. */
export type ProjView = 'cards' | 'table' | 'tree' | 'split';

interface ProjectTreeState {
  /** Plain object so persist() can roundtrip it; we wrap reads in a getter. */
  expandedMap: Record<string, true>;
  /** The "active project id" the tree's row highlight reflects.
   *  Special values:
   *    `null`       → All-captures row is active (long-standing contract)
   *    `'__inbox'`  → the virtual Inbox row (unfiled captures) is active
   *    any other    → that project id is active
   *  The `__inbox` sentinel is a UI-only key — no row with that id exists
   *  in the DB. Filtering treats it as "guides where project_id IS NULL". */
  selectedId: string | null;
  /** Page-friendly mirror of `selectedId` with explicit `'all'` / `'__inbox'`
   *  states. Mapping inside `select()`:
   *    selectedId === null        ⇒ selectedProjectId === 'all'
   *    selectedId === '__inbox'   ⇒ selectedProjectId === '__inbox'
   *    selectedId === <uuid>      ⇒ selectedProjectId === <uuid>
   *  Set to plain `null` only by callers that want the "no project picked
   *  yet" empty state (e.g. ProjectsPage on first mount before persistence
   *  hydrates). Kept in lock-step inside `select()` so the tree + page agree. */
  selectedProjectId: string | 'all' | '__inbox' | null;
  dragSource: DragSource | null;
  dropTarget: DropTarget | null;

  /** Snapshot of the nested project tree (populated by loadTree). Empty
   *  array before the first load — ProjectTree builds its own local copy
   *  too; this one is exposed for full-page consumers like ProjectsPage. */
  tree: ProjectNode[];
  /** Guides bucketed by project id. The synthetic key `'__inbox'` collects
   *  guides whose project_id is null/missing/dangling — this is the techy
   *  name for the default "no folder" landing zone every fresh capture lands
   *  in. The double-underscore prefix sorts it ahead of real project ids and
   *  signals "system" / virtual. Renamed from `'unfiled'` for consistency
   *  with the UI label and so consumers grep cleanly. */
  guidesByProject: Record<string, GuideLeaf[]>;
  /** PageSnip diagrams bucketed by project id — the diagram counterpart of
   *  `guidesByProject`, including the same synthetic `'__inbox'` key for
   *  diagrams with no project_id. Populated by loadTree() from GET
   *  /api/v1/process-studio; empty (not an error) for workspaces where
   *  PageSnip isn't entitled — see loadTree()'s .catch. */
  diagramsByProject: Record<string, DiagramLeaf[]>;

  toggleExpanded: (id: string) => void;
  setExpanded: (ids: string[]) => void;
  expandAll: (ids: string[]) => void;
  collapseAll: () => void;
  select: (id: string | null) => void;
  /** Wave-9 / Change 6a — Studio convenience. When the user opens a
   *  /guides/:id route, we want the tree to (a) highlight that snip row and
   *  (b) auto-expand its parent project so the row is visually reachable.
   *  Pass `projectId = null` for snips that live in the Inbox (we expand
   *  the synthetic `__inbox` bucket — already always rendered, so the
   *  expand step is a no-op there but kept for symmetry). */
  selectAndExpand: (snipId: string, projectId: string | null) => void;
  /** Page-level setter that distinguishes "no selection" (`null`) from
   *  "all captures" (`'all'`). The tree's own `select(id)` is the right call
   *  from inside the tree itself; ProjectsPage uses this to set 'all' too. */
  selectProject: (id: string | 'all' | '__inbox' | null) => void;
  setDragSource: (s: DragSource | null) => void;
  setDropTarget: (t: DropTarget | null) => void;
  resetDrag: () => void;
  /** Fetch projects + guides, build the nested tree, derive guidesByProject
   *  (including the `'__inbox'` bucket of unfiled guides), and commit both
   *  to the store. Safe to call repeatedly — each call is a full refresh. */
  loadTree: () => Promise<void>;

  /** BP 54 — Projects page view mode. Single source of truth (was
   *  ProjectsPage-local state + localStorage['worksnip:projectsView']
   *  before the Tree view landed). */
  projectsView: ProjView;
  setProjectsView: (v: ProjView) => void;
  /** BP 54 — bumped by ProjectsTreePanel after a successful structural
   *  mutation so the left-rail ProjectTree (which owns its own fetched
   *  copies) knows to reload. NOT persisted. ProjectTree.reload() ->
   *  loadTree() never bumps it (no loop). */
  treeMutationNonce: number;
  bumpTreeMutation: () => void;
}

/** Build nested ProjectNodes from the flat /projects/tree payload and attach
 *  each guide as a leaf under its project. Mirrors the in-component builder
 *  in ProjectTree.tsx — duplicated rather than shared because that component
 *  still owns its own local copy for tighter HMR + drag-state coupling. */
function buildTreeFromFlat(
  projects: Project[],
  guides: { id: string; title: string; public_id?: string;
            description?: string; tldr?: string; intent_summary?: string | null;
            project_id?: string | null; ordinal?: number; created_at?: string;
            user_id?: string; status?: GuideLeaf['status']; released_at?: string | null;
            kind?: string; work_status?: string | null; severity?: GuideLeaf['severity'];
            tags?: string[]; step_count?: number; thumbnail_url?: string | null }[],
  diagrams: DiagramMeta[] = [],
): { tree: ProjectNode[]; guidesByProject: Record<string, GuideLeaf[]>; diagramsByProject: Record<string, DiagramLeaf[]> } {
  const byId = new Map<string, ProjectNode>();
  projects.forEach((p) =>
    byId.set(p.id, { ...p, children: [], guides: [], diagrams: [], depth: 0 }));
  const roots: ProjectNode[] = [];
  projects.forEach((p) => {
    const n = byId.get(p.id)!;
    if (p.parent_id && byId.has(p.parent_id)) {
      byId.get(p.parent_id)!.children.push(n);
    } else {
      roots.push(n);
    }
  });

  // `__inbox` is the synthetic bucket key for guides with no project_id —
  // ALL fresh captures land here by default (the backend never auto-assigns
  // a project on finalize; see PostProcessService.js for the rationale).
  const guidesByProject: Record<string, GuideLeaf[]> = { __inbox: [] };
  guides.forEach((g) => {
    const leaf: GuideLeaf = {
      id: g.id,
      title: g.title,
      public_id: g.public_id,
      // BP 54 — second line of the Projects Tree board card (description → tldr
      // → public id). Undefined-safe: absent on the API response = id fallback.
      description: g.description,
      tldr: g.tldr,
      // BP 54 — carried straight through for the tree search haystack; see
      // GuideLeaf['intent_summary'] in types/project.ts.
      intent_summary: g.intent_summary ?? undefined,
      project_id: g.project_id ?? null,
      // BP 54 — per-folder manual sort position; drives compareGuides so a
      // drag-reorder on the Projects Tree survives a reload.
      ordinal: g.ordinal ?? 0,
      created_at: g.created_at,
      // BP 16/37 — carry lifecycle + work-tracking fields so the folder page
      // can lens-filter, show chips, and run bulk ops without a re-fetch.
      user_id: g.user_id,
      status: g.status,
      released_at: g.released_at ?? null,
      kind: g.kind,
      work_status: g.work_status ?? null,
      severity: g.severity,
      tags: Array.isArray(g.tags) ? g.tags : [],
      step_count: g.step_count,
      // BP 26 — carry the signed first-step thumbnail the /guides list
      // endpoint already returns so the project-detail cards can render the
      // same preview the Dashboard + /shared cards do.
      thumbnail_url: g.thumbnail_url ?? null,
      depth: 0,
    };
    const folder = g.project_id ? byId.get(g.project_id) : undefined;
    if (folder) {
      folder.guides.push(leaf);
      (guidesByProject[g.project_id!] ||= []).push(leaf);
    } else {
      // Includes legitimately-unfiled captures AND orphans whose project_id
      // points at a deleted/archived folder (those byId.get misses fall here
      // too, which is the intended fallback — the user sees the orphan in
      // __inbox and can re-file it).
      guidesByProject.__inbox.push(leaf);
    }
  });

  // `__inbox` mirrors guidesByProject's synthetic bucket — diagrams with no
  // project_id (or pointing at a deleted/archived folder) land here so the
  // PageSnip page's own Inbox scope has something to read; the shared tree
  // itself only renders diagrams nested under a real folder (see below).
  const diagramsByProject: Record<string, DiagramLeaf[]> = { __inbox: [] };
  diagrams.forEach((d) => {
    const leaf: DiagramLeaf = {
      id: d.id,
      title: d.title || '',
      kind: d.kind,
      project_id: d.project_id ?? null,
      depth: 0,
    };
    const folder = d.project_id ? byId.get(d.project_id) : undefined;
    if (folder) {
      folder.diagrams.push(leaf);
      (diagramsByProject[d.project_id!] ||= []).push(leaf);
    } else {
      diagramsByProject.__inbox.push(leaf);
    }
  });

  const sortRec = (list: ProjectNode[], depth: number) => {
    list.sort((a, b) => a.ordinal - b.ordinal);
    list.forEach((n, i) => {
      n.depth = depth;
      // Sprint-15 — last-sibling flag drives the └─ vs ├─ connector elbow.
      // Folders + their leaf guides are stamped together so the last leaf
      // under the last folder closes correctly.
      n.isLastSibling = i === list.length - 1;
      // Must match ProjectTree.tsx's builder exactly, or the same folder lists
      // its snips in a different order on two pages. See tree/numbering.ts.
      n.guides.sort(compareGuides);
      n.guides.forEach((g, gi) => {
        g.depth = depth + 1;
        g.isLastSibling = gi === n.guides.length - 1;
      });
      // Diagrams carry no ordinal/numbered-title convention of their own —
      // compareGuides' generic {title, created_at, ordinal} shape still
      // gives a stable, sensible order (newest-first tiebreak) without a
      // second sort routine.
      n.diagrams.sort(compareGuides);
      n.diagrams.forEach((d, di) => {
        d.depth = depth + 1;
        d.isLastSibling = di === n.diagrams.length - 1;
      });
      sortRec(n.children, depth + 1);
    });
  };
  sortRec(roots, 0);
  return { tree: roots, guidesByProject, diagramsByProject };
}

// BP 54 — legacy pre-store view-mode key (ProjectsPage used to own this in
// localStorage directly). Read once as the initial value so existing users'
// choice survives the migration into the store; an unknown/stale value
// degrades to 'cards' rather than wedging on an unrenderable view.
const LEGACY_VIEW_KEY = 'worksnip:projectsView';
function initialProjView(): ProjView {
  try {
    const v = localStorage.getItem(LEGACY_VIEW_KEY);
    return v === 'table' || v === 'tree' || v === 'split' ? v : 'cards';
  } catch { return 'cards'; }
}

export const useProjectTree = create<ProjectTreeState>()(
  persist(
    (set) => ({
      expandedMap: {},
      selectedId: null,
      // Default to 'all' so ProjectsPage lands on the All-captures view on
      // first visit rather than the empty "pick a project" prompt.
      selectedProjectId: 'all',
      dragSource: null,
      dropTarget: null,
      tree: [],
      guidesByProject: {},
      diagramsByProject: {},
      projectsView: initialProjView(),
      treeMutationNonce: 0,

      toggleExpanded: (id) => set((s) => {
        const next = { ...s.expandedMap };
        if (next[id]) delete next[id]; else next[id] = true;
        return { expandedMap: next };
      }),
      setExpanded: (ids) => set(() => {
        const m: Record<string, true> = {};
        for (const id of ids) m[id] = true;
        return { expandedMap: m };
      }),
      expandAll: (ids) => set(() => {
        const m: Record<string, true> = {};
        for (const id of ids) m[id] = true;
        return { expandedMap: m };
      }),
      collapseAll: () => set({ expandedMap: {} }),
      // `select` is the tree's intra-component API — `null` here means the
      // user clicked the "All captures" header row, so we surface that as
      // 'all' on the page-facing `selectedProjectId`. `'__inbox'` is passed
      // through as-is (the Inbox row IS a row). Pages that want the distinct
      // "no selection" state call `selectProject(null)` instead.
      select: (id) => set({
        selectedId: id,
        selectedProjectId: id === null ? 'all' : id,
      }),
      // Wave-9 / Change 6a — atomic select + parent-expand. Used by
      // CreatorStudio on mount so the rail follows the user into the snip
      // they just opened. Idempotent on the expanded set so re-opening the
      // same snip doesn't flicker the tree.
      selectAndExpand: (snipId, projectId) => set((s) => {
        const next = { ...s.expandedMap };
        if (projectId) next[projectId] = true;
        return {
          expandedMap: next,
          selectedId: snipId,
          selectedProjectId: projectId || 'all',
        };
      }),
      // Page-level setter — keeps `selectedId` in sync so the tree's row
      // highlight follows along. 'all' maps to `selectedId === null` (the
      // tree's long-standing "All captures is active" convention); '__inbox'
      // round-trips as itself so the tree highlights the Inbox row; a plain
      // null is "no project picked" and clears the tree highlight too.
      selectProject: (id) => set({
        selectedProjectId: id,
        selectedId: id === 'all' || id === null ? null : id,
      }),
      setDragSource: (s) => set({ dragSource: s }),
      setDropTarget: (t) => set({ dropTarget: t }),
      resetDrag: () => set({ dragSource: null, dropTarget: null }),

      loadTree: async () => {
        const [projects, guides, diagrams] = await Promise.all([
          projectApi.fetchTree(),
          guideTreeApi.list(),
          // Best-effort — a workspace without PageSnip entitled gets a 402/403
          // here (see requireProcessStudio); that must NOT fail the whole
          // tree load, so it degrades to "no diagrams" instead of rejecting
          // the Promise.all above.
          flowsnipApi.list().catch(() => [] as DiagramMeta[]),
        ]);
        const { tree, guidesByProject, diagramsByProject } = buildTreeFromFlat(projects, guides, diagrams);
        set((s) => {
          const patch: Partial<ProjectTreeState> = { tree, guidesByProject, diagramsByProject };
          // FE-rel-24 — the persisted selectedProjectId survives a workspace
          // switch / account change. If it points at a project id that isn't in
          // THIS tree, the page would silently filter to an empty A-only folder.
          // Validate the concrete-uuid case and fall back to 'all'.
          const pid = s.selectedProjectId;
          if (pid && pid !== 'all' && pid !== '__inbox') {
            const known = new Set<string>();
            const walk = (nodes: ProjectNode[]) =>
              nodes.forEach((n) => { known.add(n.id); walk(n.children); });
            walk(tree);
            if (!known.has(pid)) {
              patch.selectedProjectId = 'all';
              patch.selectedId = null;
            }
          }
          return patch;
        });
      },

      setProjectsView: (v) => set({ projectsView: v }),
      bumpTreeMutation: () => set((s) => ({ treeMutationNonce: s.treeMutationNonce + 1 })),
    }),
    {
      name: 'worksnip.projectTree.expanded',
      // Persist only the durable view-state — never drag state, never the
      // ephemeral hover target, never `tree`/`guidesByProject` (those are
      // network-derived and always re-fetched on mount). `treeMutationNonce`
      // is deliberately excluded too — it's a same-tab reload signal, not a
      // durable preference.
      partialize: (s) => ({
        expandedMap: s.expandedMap,
        selectedId: s.selectedId,
        selectedProjectId: s.selectedProjectId,
        projectsView: s.projectsView,
      }),
    },
  ),
);

/** Stable empty array so the no-children selector never trips re-renders. */
export const EMPTY_IDS: string[] = [];
